// Property editors for the storefront commerce blocks — `cart_checkout`,
// `my_records`, `reviews`. Every setting is a COLUMN NAME picked from
// the real transaction model (loadTransactionObject), never a value the
// browser is trusted with: the server reads the block from the saved page.
import React, { useEffect, useState } from 'react'
import type {
  Block, CartCheckoutBlock, CheckoutField, MyRecordsBlock, ReviewsBlock,
} from '@twasta/page-schema'
import { loadTransactionObject } from '@/api/designers'

type Txn = { obj_name: string; title: string }

const LABEL: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)',
  marginTop: 10, marginBottom: 4,
}
const INPUT: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 6,
  background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)',
}
const HINT: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4, lineHeight: 1.4 }
const GROUP: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 8, padding: '4px 10px 10px', marginTop: 12,
}

interface FormCols { form_no: string; title: string; columns: string[] }

/** The forms + column names of one transaction object. */
function useTxnForms(project: string, obj: string | undefined): { forms: FormCols[]; error: string } {
  const [forms, setForms] = useState<FormCols[]>([])
  const [error, setError] = useState('')
  useEffect(() => {
    if (!project || !obj) { setForms([]); setError(''); return }
    let gone = false
    loadTransactionObject(project, obj)
      .then((res) => {
        if (gone) return
        const content = res?.content
        const t = content ? (content.transaction || content) : null
        const out: FormCols[] = []
        for (const e of (t?.sql_models || []) as { sql_model?: Record<string, unknown> }[]) {
          const sm = (e?.sql_model || e) as Record<string, unknown>
          const cols = ((sm.columns as unknown[]) || []).map((c) => {
            const col = (c as { column?: Record<string, unknown> }).column || (c as Record<string, unknown>)
            return String(col.name || col.db_name || '')
          }).filter(Boolean)
          out.push({ form_no: String(sm.form_no || ''), title: String(sm.form_title || sm.main_table || ''), columns: cols })
        }
        setForms(out)
        setError(out.length ? '' : 'This object has no forms.')
      })
      .catch(() => { if (!gone) { setForms([]); setError('Could not load this object.') } })
    return () => { gone = true }
  }, [project, obj])
  return { forms, error }
}

function headerCols(forms: FormCols[]): string[] {
  return forms[0]?.columns || []
}

function formCols(forms: FormCols[], formNo: string | undefined): string[] {
  return forms.find((f) => f.form_no === String(formNo || ''))?.columns || []
}

function ObjectSelect({ value, transactions, onChange }: { value?: string; transactions: Txn[]; onChange: (v: string) => void }) {
  const known = transactions.some((t) => t.obj_name === value)
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} style={INPUT}>
      <option value="">— choose —</option>
      {value && !known && <option value={value}>{value} (not in this project)</option>}
      {transactions.map((t) => <option key={t.obj_name} value={t.obj_name}>{t.title || t.obj_name}</option>)}
    </select>
  )
}

function ColumnSelect({ value, columns, onChange, allowNone = true }: {
  value?: string; columns: string[]; onChange: (v: string) => void; allowNone?: boolean
}) {
  const known = !value || columns.some((c) => c.toLowerCase() === value.toLowerCase())
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} style={INPUT}>
      {allowNone && <option value="">— none —</option>}
      {!known && <option value={value}>{value} (missing)</option>}
      {columns.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  )
}

function Text({ label, value, placeholder, onChange, hint }: {
  label: string; value?: string | number; placeholder?: string; onChange: (v: string) => void; hint?: string
}) {
  return (
    <>
      <label style={LABEL}>{label}</label>
      <input value={value ?? ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={INPUT} />
      {hint && <div style={HINT}>{hint}</div>}
    </>
  )
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, marginTop: 10 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  )
}

/** `A=B` lines ↔ a record. */
function MapText({ label, value, onChange, hint }: {
  label: string; value?: Record<string, string>; onChange: (v: Record<string, string> | undefined) => void; hint?: string
}) {
  const [draft, setDraft] = useState(() => Object.entries(value || {}).map(([k, v]) => `${k}=${v}`).join('\n'))
  return (
    <>
      <label style={LABEL}>{label}</label>
      <textarea rows={3} value={draft} style={{ ...INPUT, fontFamily: 'monospace' }}
        onChange={(e) => {
          setDraft(e.target.value)
          const out: Record<string, string> = {}
          for (const line of e.target.value.split('\n')) {
            const i = line.indexOf('=')
            if (i > 0) out[line.slice(0, i).trim()] = line.slice(i + 1).trim()
          }
          onChange(Object.keys(out).length ? out : undefined)
        }} />
      {hint && <div style={HINT}>{hint}</div>}
    </>
  )
}

// ── cart_checkout ─────────────────────────────────────────────────────────

function FieldRow({ f, idx, count, header, address, onChange, onMove, onRemove }: {
  f: CheckoutField; idx: number; count: number; header: string[]; address: string[] | null
  onChange: (f: CheckoutField) => void; onMove: (d: -1 | 1) => void; onRemove: () => void
}) {
  const set = (p: Partial<CheckoutField>) => onChange({ ...f, ...p })
  return (
    <div style={{ ...GROUP, padding: 8, marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input value={f.key} placeholder="key" onChange={(e) => set({ key: e.target.value.replace(/[^A-Za-z0-9_]/g, '') })} style={{ ...INPUT, width: '40%' }} />
        <input value={f.label} placeholder="Label" onChange={(e) => set({ label: e.target.value })} style={INPUT} />
      </div>
      <label style={LABEL}>Order column</label>
      <ColumnSelect value={f.column} columns={header} onChange={(v) => set({ column: v || undefined })} />
      {address && (
        <>
          <label style={LABEL}>Saved-address column</label>
          <ColumnSelect value={f.addressColumn} columns={address} onChange={(v) => set({ addressColumn: v || undefined })} />
        </>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <div>
          <label style={LABEL}>Kind</label>
          <select value={f.kind || 'text'} onChange={(e) => set({ kind: e.target.value as CheckoutField['kind'] })} style={INPUT}>
            {['text', 'email', 'tel', 'textarea', 'postcode'].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div>
          <label style={LABEL}>Group</label>
          <select value={f.group || ''} onChange={(e) => set({ group: (e.target.value || undefined) as CheckoutField['group'] })} style={INPUT}>
            <option value="">auto</option><option value="contact">contact</option><option value="address">address</option>
          </select>
        </div>
        <div>
          <label style={LABEL}>Prefill</label>
          <select value={f.prefill || ''} onChange={(e) => set({ prefill: (e.target.value || undefined) as CheckoutField['prefill'] })} style={INPUT}>
            <option value="">—</option><option value="name">signed-in name</option><option value="email">signed-in email</option>
          </select>
        </div>
        <div>
          <label style={LABEL}>Autocomplete</label>
          <input value={f.autocomplete || ''} placeholder="address-line1" onChange={(e) => set({ autocomplete: e.target.value || undefined })} style={INPUT} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, fontSize: 13 }}>
        <label><input type="checkbox" checked={!!f.required} onChange={(e) => set({ required: e.target.checked })} /> Required</label>
        <span style={{ flex: 1 }} />
        <button type="button" disabled={idx === 0} onClick={() => onMove(-1)}>↑</button>
        <button type="button" disabled={idx === count - 1} onClick={() => onMove(1)}>↓</button>
        <button type="button" onClick={onRemove}>Remove</button>
      </div>
    </div>
  )
}

function CartCheckoutEditor({ block, project, transactions, onChange }: {
  block: CartCheckoutBlock; project: string; transactions: Txn[]; onChange: (b: Block) => void
}) {
  const order = block.order || { object: '', lines: { detailFormNo: '', skuColumn: '', qtyColumn: '' } }
  const lines = order.lines || { detailFormNo: '', skuColumn: '', qtyColumn: '' }
  const { forms, error } = useTxnForms(project, order.object)
  const addr = useTxnForms(project, block.addresses?.object)
  const promo = block.promo
  const codes = useTxnForms(project, promo?.lookup?.object)
  const set = (p: Partial<CartCheckoutBlock>) => onChange({ ...block, ...p } as Block)
  type Promo = NonNullable<CartCheckoutBlock['promo']>
  const setPromo = (p: Partial<Promo>) => set({ promo: { ...(promo as Promo), ...p } })
  const setLookup = (p: Partial<Promo['lookup']>) => setPromo({ lookup: { ...(promo as Promo).lookup, ...p } })
  const setOrder = (p: Partial<CartCheckoutBlock['order']>) => set({ order: { ...order, ...p } })
  const setLines = (p: Partial<typeof lines>) => setOrder({ lines: { ...lines, ...p } })
  const header = headerCols(forms)
  const detail = formCols(forms, lines.detailFormNo)
  const fields = block.fields || []
  const setFields = (next: CheckoutField[]) => set({ fields: next })
  return (
    <div>
      <label style={LABEL}>Order object</label>
      <ObjectSelect value={order.object} transactions={transactions} onChange={(v) => setOrder({ object: v })} />
      {error && <div style={{ ...HINT, color: 'var(--color-error)' }}>{error}</div>}
      <div style={HINT}>The order is saved through this object's own logic — prices, tax and stock come from it, never from the basket.</div>

      <div style={GROUP}>
        <label style={LABEL}>Lines form</label>
        <select value={lines.detailFormNo || ''} onChange={(e) => setLines({ detailFormNo: e.target.value })} style={INPUT}>
          <option value="">— choose —</option>
          {forms.slice(1).map((f) => <option key={f.form_no} value={f.form_no}>{f.form_no} · {f.title}</option>)}
        </select>
        <label style={LABEL}>Item code column</label>
        <ColumnSelect value={lines.skuColumn} columns={detail} allowNone={false} onChange={(v) => setLines({ skuColumn: v })} />
        <label style={LABEL}>Quantity column</label>
        <ColumnSelect value={lines.qtyColumn} columns={detail} allowNone={false} onChange={(v) => setLines({ qtyColumn: v })} />
        <label style={LABEL}>Booking start / end / units (optional)</label>
        <ColumnSelect value={lines.startColumn} columns={detail} onChange={(v) => setLines({ startColumn: v || undefined })} />
        <ColumnSelect value={lines.endColumn} columns={detail} onChange={(v) => setLines({ endColumn: v || undefined })} />
        <ColumnSelect value={lines.unitsColumn} columns={detail} onChange={(v) => setLines({ unitsColumn: v || undefined })} />
      </div>

      <label style={LABEL}>Fields</label>
      {fields.map((f, i) => (
        <FieldRow key={i} f={f} idx={i} count={fields.length} header={header}
          address={block.addresses?.object ? headerCols(addr.forms) : null}
          onChange={(nf) => setFields(fields.map((x, j) => (j === i ? nf : x)))}
          onMove={(d) => { const n = [...fields]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x); setFields(n) }}
          onRemove={() => setFields(fields.filter((_, j) => j !== i))} />
      ))}
      <button type="button" style={{ marginTop: 8 }}
        onClick={() => setFields([...fields, { key: `field${fields.length + 1}`, label: 'New field' }])}>+ Add field</button>

      <MapText label="Fixed order values (COLUMN=value)" value={order.set} onChange={(v) => setOrder({ set: v })}
        hint="Applied by the server — e.g. ORDER_STATUS=NEW." />
      <label style={LABEL}>Composed address column</label>
      <ColumnSelect value={order.composeAddress?.column} columns={header}
        onChange={(v) => setOrder({ composeAddress: v ? { column: v, template: order.composeAddress?.template || '' } : undefined })} />
      {order.composeAddress?.column && (
        <Text label="Address template" value={order.composeAddress.template} placeholder="{{line1}}, {{city}} {{pin}}"
          onChange={(v) => setOrder({ composeAddress: { column: order.composeAddress!.column, template: v } })} hint="Tokens are field keys." />
      )}
      <label style={LABEL}>Order number column (confirmation)</label>
      <ColumnSelect value={order.referenceColumn} columns={header} onChange={(v) => setOrder({ referenceColumn: v || undefined })} />
      <label style={LABEL}>Total column (confirmation)</label>
      <ColumnSelect value={order.totalColumn} columns={header} onChange={(v) => setOrder({ totalColumn: v || undefined })} />

      <Text label="Order id variable" value={block.orderIdVar} placeholder="orderId" onChange={(v) => set({ orderIdVar: v })}
        hint="A payment_checkout on this page reads it through its txnIdVar." />
      <Check label="Require sign-in" checked={!!block.requireSignIn} onChange={(v) => set({ requireSignIn: v })} />
      <label style={LABEL}>Empty the basket</label>
      <select value={block.clearCart || 'on_order'} onChange={(e) => set({ clearCart: e.target.value as CartCheckoutBlock['clearCart'] })} style={INPUT}>
        <option value="on_order">when the order is placed</option><option value="never">never</option>
      </select>

      <div style={GROUP}>
        <label style={LABEL}>Saved addresses object (per account)</label>
        <ObjectSelect value={block.addresses?.object} transactions={transactions}
          onChange={(v) => set({ addresses: v ? { ...(block.addresses || {}), object: v } : undefined })} />
        {block.addresses?.object && (
          <>
            <label style={LABEL}>Label column</label>
            <ColumnSelect value={block.addresses.labelColumn} columns={headerCols(addr.forms)}
              onChange={(v) => set({ addresses: { ...block.addresses!, labelColumn: v || undefined } })} />
            <Check label="Offer to save a new address" checked={block.addresses.allowSave !== false}
              onChange={(v) => set({ addresses: { ...block.addresses!, allowSave: v } })} />
          </>
        )}
      </div>

      <Check label="Show order summary" checked={block.showSummary !== false} onChange={(v) => set({ showSummary: v })} />
      {block.showSummary !== false && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <Text label="Tax %" value={block.summaryRules?.taxPercent} onChange={(v) => set({ summaryRules: { ...(block.summaryRules || {}), taxPercent: v ? Number(v) : undefined } })} />
          <Text label="Shipping" value={block.summaryRules?.shippingFlat} onChange={(v) => set({ summaryRules: { ...(block.summaryRules || {}), shippingFlat: v ? Number(v) : undefined } })} />
          <Text label="Free shipping over" value={block.summaryRules?.shippingFreeOver} onChange={(v) => set({ summaryRules: { ...(block.summaryRules || {}), shippingFreeOver: v ? Number(v) : undefined } })} />
        </div>
      )}
      <div style={GROUP}>
        <Check label="Discount / creator code" checked={!!promo}
          onChange={(v) => set({ promo: v ? { column: '', lookup: { object: '', codeColumn: '', percentColumn: '' } } : undefined })} />
        {promo && (
          <>
            <label style={LABEL}>Order column the code is saved into</label>
            <ColumnSelect value={promo.column} columns={header} allowNone={false} onChange={(v) => setPromo({ column: v })} />
            <label style={LABEL}>Codes object (shared, not per account)</label>
            <ObjectSelect value={promo.lookup?.object} transactions={transactions} onChange={(v) => setLookup({ object: v })} />
            {codes.error && <div style={{ ...HINT, color: 'var(--color-error)' }}>{codes.error}</div>}
            <label style={LABEL}>Code column</label>
            <ColumnSelect value={promo.lookup?.codeColumn} columns={headerCols(codes.forms)} allowNone={false} onChange={(v) => setLookup({ codeColumn: v })} />
            <label style={LABEL}>Percent off column</label>
            <ColumnSelect value={promo.lookup?.percentColumn} columns={headerCols(codes.forms)} allowNone={false} onChange={(v) => setLookup({ percentColumn: v })} />
            <label style={LABEL}>Shared by column (optional)</label>
            <ColumnSelect value={promo.lookup?.labelColumn} columns={headerCols(codes.forms)} onChange={(v) => setLookup({ labelColumn: v || undefined })} />
            <label style={LABEL}>Active column + value (optional)</label>
            <ColumnSelect value={promo.lookup?.activeColumn} columns={headerCols(codes.forms)} onChange={(v) => setLookup({ activeColumn: v || undefined })} />
            {promo.lookup?.activeColumn && (
              <Text label="Active value" value={promo.lookup.activeValue} placeholder="Y" onChange={(v) => setLookup({ activeValue: v || undefined })} />
            )}
            <label style={LABEL}>Valid from / valid to / minimum order (optional)</label>
            <ColumnSelect value={promo.lookup?.validFromColumn} columns={headerCols(codes.forms)} onChange={(v) => setLookup({ validFromColumn: v || undefined })} />
            <ColumnSelect value={promo.lookup?.validToColumn} columns={headerCols(codes.forms)} onChange={(v) => setLookup({ validToColumn: v || undefined })} />
            <ColumnSelect value={promo.lookup?.minOrderColumn} columns={headerCols(codes.forms)} onChange={(v) => setLookup({ minOrderColumn: v || undefined })} />
            <Text label="Box label" value={promo.label} placeholder="Discount code" onChange={(v) => setPromo({ label: v || undefined })} />
            <Text label="Placeholder" value={promo.placeholder} onChange={(v) => setPromo({ placeholder: v || undefined })} />
            <Text label="Applied text" value={promo.appliedText} placeholder="{{code}} applied · shared by {{label}}"
              onChange={(v) => setPromo({ appliedText: v || undefined })} hint="Tokens: {{code}}, {{label}}, {{percent}}." />
            <Text label="Discount line" value={promo.discountLabel} placeholder="Discount ({{percent}}%)"
              onChange={(v) => setPromo({ discountLabel: v || undefined })} />
            <div style={HINT}>Shown in the order summary. The order's own logic still validates the code and prices the discount.</div>
          </>
        )}
      </div>
      <Text label="Go to after ordering" value={block.onSuccessNavigateTo} placeholder="/orders/{{reference}}" onChange={(v) => set({ onSuccessNavigateTo: v || undefined })} />
      <Text label="Heading" value={block.heading} onChange={(v) => set({ heading: v || undefined })} />
      <Text label="Button label" value={block.submitLabel} placeholder="Place order" onChange={(v) => set({ submitLabel: v || undefined })} />
      <Text label="Empty basket text" value={block.emptyText} onChange={(v) => set({ emptyText: v || undefined })} />
      <Text label="Empty basket link" value={block.emptyCtaHref} placeholder="/shop" onChange={(v) => set({ emptyCtaHref: v || undefined })} />
      <Text label="Success title" value={block.successTitle} onChange={(v) => set({ successTitle: v || undefined })} />
      <Text label="Success message" value={block.successBody} onChange={(v) => set({ successBody: v || undefined })} />
      <div style={{ ...HINT, fontWeight: 600, marginTop: 6 }}>Layout</div>
      <Check label="Button at the foot of the order summary" checked={!!block.submitInSummary}
        onChange={(v) => set({ submitInSummary: v || undefined })} />
      <Check label='Signed-in shopper sees "Signed in as … · Change"' checked={block.signedInContact === 'card'}
        onChange={(v) => set({ signedInContact: v ? 'card' : undefined })} />
      <div style={HINT}>Only when every required contact field is already filled from the account.</div>
      <Text label="Summary heading" value={block.summaryHeading} placeholder="Order summary" onChange={(v) => set({ summaryHeading: v || undefined })} />
      <Text label="Total label" value={block.summaryTotalLabel} placeholder="Estimated total" onChange={(v) => set({ summaryTotalLabel: v || undefined })} />
      <Text label="Note under the summary" value={block.summaryNote} placeholder="Prices, tax and delivery are confirmed when the order is placed."
        onChange={(v) => set({ summaryNote: v || undefined })} />
      <div style={{ ...HINT, fontWeight: 600, marginTop: 6 }}>Note under the forms</div>
      <Text label="Title" value={block.note?.title} onChange={(v) => set({ note: { ...(block.note || {}), title: v || undefined } })} />
      <Text label="Text" value={block.note?.body} onChange={(v) => set({ note: { ...(block.note || {}), body: v || undefined } })} />
      <Text label="Icon" value={block.note?.icon} placeholder="tag" onChange={(v) => set({ note: { ...(block.note || {}), icon: v || undefined } })} />
    </div>
  )
}

// ── my_records ─────────────────────────────────────────────────────────────

function MyRecordsEditor({ block, project, transactions, onChange }: {
  block: MyRecordsBlock; project: string; transactions: Txn[]; onChange: (b: Block) => void
}) {
  const { forms, error } = useTxnForms(project, block.object)
  const img = useTxnForms(project, block.lines?.imageFrom?.object)
  const set = (p: Partial<MyRecordsBlock>) => onChange({ ...block, ...p } as Block)
  const cols = block.columns || { reference: '', date: '' }
  const setCols = (p: Partial<MyRecordsBlock['columns']>) => set({ columns: { ...cols, ...p } })
  const lines = block.lines
  const setLines = (p: Partial<NonNullable<MyRecordsBlock['lines']>>) =>
    set({ lines: { ...(lines || { detailFormNo: '', sku: '', name: '', qty: '' }), ...p } })
  const header = headerCols(forms)
  const detail = formCols(forms, lines?.detailFormNo)
  const reorder = block.reorder === false ? null : (block.reorder || null)
  return (
    <div>
      <label style={LABEL}>Order object (kept per account)</label>
      <ObjectSelect value={block.object} transactions={transactions} onChange={(v) => set({ object: v })} />
      {error && <div style={{ ...HINT, color: 'var(--color-error)' }}>{error}</div>}
      {(['reference', 'date', 'status', 'paymentStatus', 'total'] as const).map((k) => (
        <React.Fragment key={k}>
          <label style={LABEL}>{k === 'paymentStatus' ? 'Payment status' : k[0].toUpperCase() + k.slice(1)} column</label>
          <ColumnSelect value={cols[k]} columns={header} allowNone={k !== 'reference' && k !== 'date'} onChange={(v) => setCols({ [k]: v || undefined })} />
        </React.Fragment>
      ))}
      <MapText label="Status tones (VALUE=success|warning|error|info|neutral)" value={block.statusTones as Record<string, string>}
        onChange={(v) => set({ statusTones: v as MyRecordsBlock['statusTones'] })} />
      <Text label="Currency prefix" value={block.currencyPrefix} placeholder="₹" onChange={(v) => set({ currencyPrefix: v || undefined })} />
      <div style={GROUP}>
        <label style={LABEL}>Lines form</label>
        <select value={lines?.detailFormNo || ''} onChange={(e) => (e.target.value ? setLines({ detailFormNo: e.target.value }) : set({ lines: undefined }))} style={INPUT}>
          <option value="">— no lines —</option>
          {forms.slice(1).map((f) => <option key={f.form_no} value={f.form_no}>{f.form_no} · {f.title}</option>)}
        </select>
        {lines?.detailFormNo && (['sku', 'name', 'qty', 'unitPrice', 'amount', 'image'] as const).map((k) => (
          <React.Fragment key={k}>
            <label style={LABEL}>{k} column</label>
            <ColumnSelect value={lines[k]} columns={detail} allowNone={!['sku', 'name', 'qty'].includes(k)} onChange={(v) => setLines({ [k]: v || undefined })} />
          </React.Fragment>
        ))}
        {lines?.detailFormNo && (
          <>
            <label style={LABEL}>Picture from object</label>
            <ObjectSelect value={lines.imageFrom?.object} transactions={transactions}
              onChange={(v) => setLines({ imageFrom: v ? { object: v, keyColumn: lines.imageFrom?.keyColumn || '', imageColumn: lines.imageFrom?.imageColumn || '' } : undefined })} />
            {lines.imageFrom?.object && (
              <>
                <label style={LABEL}>Its item code / picture columns</label>
                <ColumnSelect value={lines.imageFrom.keyColumn} columns={headerCols(img.forms)} onChange={(v) => setLines({ imageFrom: { ...lines.imageFrom!, keyColumn: v } })} />
                <ColumnSelect value={lines.imageFrom.imageColumn} columns={headerCols(img.forms)} onChange={(v) => setLines({ imageFrom: { ...lines.imageFrom!, imageColumn: v } })} />
              </>
            )}
            <Text label="Product link" value={lines.hrefTemplate} placeholder="/product/{{sku}}" onChange={(v) => setLines({ hrefTemplate: v || undefined })} />
          </>
        )}
      </div>
      <Check label="Offer “Buy again”" checked={!!reorder} onChange={(v) => set({ reorder: v ? { label: 'Buy again', cartHref: '/cart' } : false })} />
      {reorder && (
        <>
          <Text label="Buy-again label" value={reorder.label} onChange={(v) => set({ reorder: { ...reorder, label: v } })} />
          <Text label="Then go to" value={reorder.cartHref} placeholder="/cart" onChange={(v) => set({ reorder: { ...reorder, cartHref: v } })} />
        </>
      )}
      <Text label="Invoice link" value={block.invoiceHref} placeholder="/invoice?id={{id}}" onChange={(v) => set({ invoiceHref: v || undefined })} />
      <Text label="Orders per page" value={block.pageSize} placeholder="10" onChange={(v) => set({ pageSize: v ? Math.max(1, Math.min(25, Number(v) || 10)) : undefined })} />
      <Text label="Heading" value={block.heading} onChange={(v) => set({ heading: v || undefined })} />
      <Text label="When there are none" value={block.emptyText} onChange={(v) => set({ emptyText: v || undefined })} />
      <Text label="Empty link label / href" value={block.emptyCtaLabel} onChange={(v) => set({ emptyCtaLabel: v || undefined })} />
      <input value={block.emptyCtaHref || ''} placeholder="/shop" onChange={(e) => set({ emptyCtaHref: e.target.value || undefined })} style={{ ...INPUT, marginTop: 4 }} />
    </div>
  )
}

// ── reviews ───────────────────────────────────────────────────────

function ReviewsEditor({ block, project, transactions, onChange }: {
  block: ReviewsBlock; project: string; transactions: Txn[]; onChange: (b: Block) => void
}) {
  const { forms, error } = useTxnForms(project, block.object)
  const chk = useTxnForms(project, block.recordCheck?.object)
  const buy = useTxnForms(project, block.purchaseCheck?.object)
  const set = (p: Partial<ReviewsBlock>) => onChange({ ...block, ...p } as Block)
  const cols = block.columns || { record: '', rating: '', body: '', status: '' }
  const setCols = (p: Partial<ReviewsBlock['columns']>) => set({ columns: { ...cols, ...p } })
  const header = headerCols(forms)
  const pc = block.purchaseCheck
  const setPc = (p: Partial<NonNullable<ReviewsBlock['purchaseCheck']>>) =>
    set({ purchaseCheck: { ...(pc || { object: '', recordColumn: '' }), ...p } })
  return (
    <div>
      <label style={LABEL}>Review object</label>
      <ObjectSelect value={block.object} transactions={transactions} onChange={(v) => set({ object: v })} />
      {error && <div style={{ ...HINT, color: 'var(--color-error)' }}>{error}</div>}
      <Text label="Record reviewed" value={block.record} placeholder="{{product.PRODUCT_CODE}}" onChange={(v) => set({ record: v })} />
      {(['record', 'rating', 'body', 'status', 'title', 'authorName', 'authorId', 'date', 'verified'] as const).map((k) => (
        <React.Fragment key={k}>
          <label style={LABEL}>{k} column</label>
          <ColumnSelect value={cols[k]} columns={header} allowNone={!['record', 'rating', 'body', 'status'].includes(k)}
            onChange={(v) => setCols({ [k]: v || undefined })} />
        </React.Fragment>
      ))}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <Text label="Approved value" value={block.approvedValue} placeholder="APPROVED" onChange={(v) => set({ approvedValue: v })} />
        <Text label="Pending value" value={block.pendingValue} placeholder="PENDING" onChange={(v) => set({ pendingValue: v })} />
      </div>
      <Check label="Moderate new reviews" checked={block.moderate !== false} onChange={(v) => set({ moderate: v })} />
      <label style={LABEL}>Who may write one</label>
      <select value={block.submit || 'signed_in'} onChange={(e) => set({ submit: e.target.value as ReviewsBlock['submit'] })} style={INPUT}>
        <option value="signed_in">signed-in shoppers</option><option value="anyone">anyone</option><option value="off">nobody</option>
      </select>
      <Check label="One review per account" checked={block.onePerAccount !== false} onChange={(v) => set({ onePerAccount: v })} />
      <div style={GROUP}>
        <Check label="Verified buyers only" checked={!!pc} onChange={(v) => set({ purchaseCheck: v ? { object: '', recordColumn: '' } : undefined })} />
        {pc && (
          <>
            <label style={LABEL}>Purchases object (kept per account)</label>
            <ObjectSelect value={pc.object} transactions={transactions} onChange={(v) => setPc({ object: v })} />
            <label style={LABEL}>Lines form (optional)</label>
            <select value={pc.detailFormNo || ''} onChange={(e) => setPc({ detailFormNo: e.target.value || undefined })} style={INPUT}>
              <option value="">— header —</option>
              {buy.forms.slice(1).map((f) => <option key={f.form_no} value={f.form_no}>{f.form_no} · {f.title}</option>)}
            </select>
            <label style={LABEL}>Item column</label>
            <ColumnSelect value={pc.recordColumn} allowNone={false} columns={pc.detailFormNo ? formCols(buy.forms, pc.detailFormNo) : headerCols(buy.forms)} onChange={(v) => setPc({ recordColumn: v })} />
            <label style={LABEL}>Order status column (optional)</label>
            <ColumnSelect value={pc.statusColumn} columns={headerCols(buy.forms)} onChange={(v) => setPc({ statusColumn: v || undefined })} />
            {pc.statusColumn && (
              <Text label="Counts when status is (comma-separated)" value={(pc.statusValues || []).join(', ')} placeholder="DELIVERED, PAID"
                onChange={(v) => setPc({ statusValues: v.split(',').map((x) => x.trim()).filter(Boolean) })} />
            )}
            <Text label="Verified value" value={block.verifiedValue} placeholder="Y" onChange={(v) => set({ verifiedValue: v || undefined })} />
          </>
        )}
      </div>
      <label style={LABEL}>Check the record exists in</label>
      <ObjectSelect value={block.recordCheck?.object} transactions={transactions}
        onChange={(v) => set({ recordCheck: v ? { object: v, column: block.recordCheck?.column || '' } : undefined })} />
      {block.recordCheck?.object && (
        <ColumnSelect value={block.recordCheck.column} columns={headerCols(chk.forms)} allowNone={false}
          onChange={(v) => set({ recordCheck: { ...block.recordCheck!, column: v } })} />
      )}
      <Text label="Reviews per page" value={block.pageSize} placeholder="10" onChange={(v) => set({ pageSize: v ? Number(v) || undefined : undefined })} />
      <Text label="Heading" value={block.heading} onChange={(v) => set({ heading: v || undefined })} />
      <Text label="Form heading" value={block.formHeading} onChange={(v) => set({ formHeading: v || undefined })} />
      <Text label="When there are none" value={block.emptyText} onChange={(v) => set({ emptyText: v || undefined })} />
      <Text label="Button label" value={block.submitLabel} onChange={(v) => set({ submitLabel: v || undefined })} />
      <Check label="Show rating summary" checked={block.showSummary !== false} onChange={(v) => set({ showSummary: v })} />
    </div>
  )
}

/** One dispatch for the three blocks (PropertyPanel mounts this). */
export function CommerceBlockProps({ block, project, transactions, onChange }: {
  block: Block; project: string; transactions: Txn[]; onChange: (b: Block) => void
}) {
  if (block.type === 'cart_checkout') return <CartCheckoutEditor block={block} project={project} transactions={transactions} onChange={onChange} />
  if (block.type === 'my_records') return <MyRecordsEditor block={block} project={project} transactions={transactions} onChange={onChange} />
  if (block.type === 'reviews') return <ReviewsEditor block={block} project={project} transactions={transactions} onChange={onChange} />
  return null
}

export const COMMERCE_BLOCK_TITLES: Record<string, string> = {
  cart_checkout: 'Checkout', my_records: 'My orders', reviews: 'Product reviews',
}
